import React, { useState, useMemo } from 'react';
import { Split } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter, SheetDescription } from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ProjectSelector } from './ProjectSelector';
import { CostCentreSelector } from './CostCentreSelector';
import type { TimesheetEntry, Project, CostCentre } from '@/types/timesheet';
import { cn } from '@/lib/utils';

interface SplitRowSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry: TimesheetEntry | null;
  projects: Project[];
  costCentres: CostCentre[];
  onSplit: (originalEntry: TimesheetEntry, newEntry: Partial<TimesheetEntry>, splitRatio: number) => void;
}

type SplitRatio = 50 | 60 | 70 | 'custom';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const SplitRowSheet: React.FC<SplitRowSheetProps> = ({
  open,
  onOpenChange,
  entry,
  projects,
  costCentres,
  onSplit,
}) => {
  const [selectedRatio, setSelectedRatio] = useState<SplitRatio>(50);
  const [customRatio, setCustomRatio] = useState<string>('50');
  const [newProjectId, setNewProjectId] = useState<string>('');
  const [newCostCentreId, setNewCostCentreId] = useState<string>('');
  const [newDescription, setNewDescription] = useState<string>('');

  // Reset state when entry changes or sheet opens
  React.useEffect(() => {
    if (open && entry) {
      setSelectedRatio(50);
      setCustomRatio('50');
      setNewProjectId(entry.projectId);
      setNewCostCentreId(entry.costCentreId);
      setNewDescription(entry.description);
    }
  }, [open, entry]);

  // Get the actual split ratio value
  const actualRatio = useMemo(() => {
    if (selectedRatio === 'custom') {
      const parsed = parseFloat(customRatio);
      return isNaN(parsed) ? 50 : Math.max(1, Math.min(99, parsed));
    }
    return selectedRatio;
  }, [selectedRatio, customRatio]);

  // Calculate split hours preview
  const splitPreview = useMemo(() => {
    if (!entry) return null;

    const ratio = actualRatio / 100;
    const newRowHours: Record<string, number> = {};
    const originalRowHours: Record<string, number> = {};

    DAYS.forEach(day => {
      const originalHours = entry.hours[day];
      const newHours = Math.round(originalHours * ratio * 100) / 100; // Round to 2 decimals
      const remainingHours = Math.round((originalHours - newHours) * 100) / 100;

      newRowHours[day] = newHours;
      originalRowHours[day] = remainingHours;
    });

    return { newRowHours, originalRowHours };
  }, [entry, actualRatio]);

  // Calculate totals
  const totals = useMemo(() => {
    if (!entry || !splitPreview) return { original: 0, new: 0, total: 0 };

    const originalTotal = DAYS.reduce((sum, day) => sum + entry.hours[day], 0);
    const newTotal = DAYS.reduce((sum, day) => sum + (splitPreview.newRowHours[day] || 0), 0);
    const remainingTotal = DAYS.reduce((sum, day) => sum + (splitPreview.originalRowHours[day] || 0), 0);

    return {
      original: Math.round(remainingTotal * 100) / 100,
      new: Math.round(newTotal * 100) / 100,
      total: Math.round(originalTotal * 100) / 100,
    };
  }, [entry, splitPreview]);

  const handleSplit = () => {
    if (!entry || !splitPreview) return;

    const newEntry: Partial<TimesheetEntry> = {
      projectId: newProjectId,
      costCentreId: newCostCentreId,
      description: newDescription,
      hours: {
        monday: splitPreview.newRowHours.monday || 0,
        tuesday: splitPreview.newRowHours.tuesday || 0,
        wednesday: splitPreview.newRowHours.wednesday || 0,
        thursday: splitPreview.newRowHours.thursday || 0,
        friday: splitPreview.newRowHours.friday || 0,
        saturday: splitPreview.newRowHours.saturday || 0,
        sunday: splitPreview.newRowHours.sunday || 0,
      },
      // Split tracking metadata
      splitFromEntryId: entry.id > 0 ? entry.id : undefined, // Only set if original has a real ID
      splitRatio: actualRatio,
      splitAt: new Date(),
    };

    onSplit(entry, newEntry, actualRatio / 100);
    onOpenChange(false);
  };

  const project = entry ? projects.find(p => p.id === entry.projectId) : null;
  const newProject = projects.find(p => p.id === newProjectId);

  const isValid = newProjectId && newCostCentreId;

  if (!entry) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[600px] flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4">
          <SheetTitle className="flex items-center gap-2">
            <Split className="h-5 w-5" />
            Split Row
          </SheetTitle>
          <SheetDescription>
            Split this timesheet row into two rows with divided hours
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 space-y-6 pb-6">
          {/* Original Row Details */}
          <div className="rounded-lg border bg-muted/50 p-4 space-y-2">
            <h3 className="font-semibold text-sm">Original Row</h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Project:</span>
                <span className="font-medium">{project?.code} - {project?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Cost Centre:</span>
                <span className="font-medium">
                  {costCentres.find(cc => cc.id === entry.costCentreId)?.code}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Description:</span>
                <span className="font-medium truncate max-w-[300px]">{entry.description || '(empty)'}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-muted-foreground">Total Hours:</span>
                <span className="font-bold">{totals.total}h</span>
              </div>
            </div>
          </div>

          {/* Split Ratio Selection */}
          <div className="space-y-3">
            <Label>Split Ratio</Label>
            <div className="grid grid-cols-4 gap-2">
              <Button
                type="button"
                variant={selectedRatio === 50 ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedRatio(50)}
                className="flex flex-col h-auto py-2"
              >
                <span className="font-bold">50/50</span>
                <span className="text-xs opacity-70">Equal</span>
              </Button>
              <Button
                type="button"
                variant={selectedRatio === 60 ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedRatio(60)}
                className="flex flex-col h-auto py-2"
              >
                <span className="font-bold">60/40</span>
                <span className="text-xs opacity-70">Majority</span>
              </Button>
              <Button
                type="button"
                variant={selectedRatio === 70 ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedRatio(70)}
                className="flex flex-col h-auto py-2"
              >
                <span className="font-bold">70/30</span>
                <span className="text-xs opacity-70">Heavy</span>
              </Button>
              <Button
                type="button"
                variant={selectedRatio === 'custom' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedRatio('custom')}
                className="flex flex-col h-auto py-2"
              >
                <span className="font-bold">Custom</span>
                <span className="text-xs opacity-70">%</span>
              </Button>
            </div>

            {selectedRatio === 'custom' && (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  max="99"
                  value={customRatio}
                  onChange={(e) => setCustomRatio(e.target.value)}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">
                  % for new row ({100 - actualRatio}% remains in original)
                </span>
              </div>
            )}
          </div>

          {/* Hours Preview */}
          <div className="space-y-3">
            <Label>Hours Preview</Label>
            <div className="rounded-lg border">
              <div className="grid grid-cols-8 gap-1 p-2 text-xs font-medium border-b bg-muted/50">
                <div></div>
                {DAY_LABELS.map(day => (
                  <div key={day} className="text-center">{day}</div>
                ))}
              </div>
              <div className="p-2 space-y-1">
                <div className="grid grid-cols-8 gap-1 text-xs">
                  <div className="text-muted-foreground">New:</div>
                  {DAYS.map(day => (
                    <div key={day} className="text-center font-medium text-blue-600">
                      {splitPreview?.newRowHours[day] || 0}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-8 gap-1 text-xs">
                  <div className="text-muted-foreground">Orig:</div>
                  {DAYS.map(day => (
                    <div key={day} className="text-center font-medium">
                      {splitPreview?.originalRowHours[day] || 0}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-between text-sm px-2">
              <span className="text-muted-foreground">Totals:</span>
              <div className="flex gap-4">
                <span className="text-blue-600 font-medium">New: {totals.new}h</span>
                <span className="font-medium">Original: {totals.original}h</span>
              </div>
            </div>
          </div>

          {/* New Row Configuration */}
          <div className="space-y-4 rounded-lg border p-4 bg-blue-50/50 dark:bg-blue-950/20">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-blue-600"></span>
              New Row Configuration
            </h3>

            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="new-project">Project</Label>
                <ProjectSelector
                  projects={projects}
                  value={newProjectId}
                  onChange={setNewProjectId}
                  placeholder="Select project..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-cost-centre">Cost Centre</Label>
                <CostCentreSelector
                  costCentres={costCentres}
                  value={newCostCentreId}
                  onChange={setNewCostCentreId}
                  placeholder="Select cost centre..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-description">Description</Label>
                <Textarea
                  id="new-description"
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder="Enter description..."
                  rows={3}
                  className="resize-none"
                />
              </div>
            </div>
          </div>
        </div>

        <SheetFooter className="sticky bottom-0 border-t bg-background px-6 py-4 gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSplit}
            disabled={!isValid}
            className="gap-2"
          >
            <Split className="h-4 w-4" />
            Split Row
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
